import * as React from "react";
import { PER_PAGE } from "../api";
import {
  computeRealtimeStream,
  type MergedTimelineEntry,
} from "../domains/MergedTimelineEntry";
import type { HTTPErrors } from "../errors";
import { useQueryError } from "./http/useQueryError";
import { useOnMainScrollAreaScroll } from "./useMainScrollAreaScroll";

// 1 つの取得ソース（学習記録）の取得結果。
// 呼び出し側が useInfiniteQuery 等の戻り値から組み立てて注入する。
// AI 質問統合表示の凍結（005-freeze-ai-question-timeline）に伴い、学習記録単一ソースに縮約。
// 凍結解除時は AI 質問ソース（aiSource）を再度追加する（proposal「復帰時」）。
type TimelineSource<T> = {
  // 取得済みエントリ（全ページ・時系列降順）
  items: T[];
  // 次ページが存在するか
  hasNextPage: boolean;
  // 次ページ取得中か
  isFetchingNextPage: boolean;
  // 次ページ取得
  fetchNextPage: () => void;
  // 初回ローディング中か
  isLoading: boolean;
  // 取得エラー（通信エラー通知に使う）
  error: HTTPErrors | null;
};

type StudyRecordLike = { recordedAt: string };

type Props<S extends StudyRecordLike> = {
  studySource: TimelineSource<S>;
  // この値が変わると表示ページ数を初期化する（フィルタ変更・対象日変更など）。
  resetKey?: unknown;
};

// 学習記録ソースを時系列降順で表示し、無限スクロールで増分表示する共通フック。
// 取得（どのエンドポイントを叩くか）・ドリル進捗などソース固有の処理は呼び出し側が担い、
// ここは「取得結果（source）の注入」を受けてバッファ・スクロール・エラー通知のみを行う。
// これにより校舎リアルタイムタブと生徒タイムラインの双方から再利用できる。
export const useMergedTimeline = <S extends StudyRecordLike>({
  studySource,
  resetKey,
}: Props<S>): {
  displayed: MergedTimelineEntry<S>[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
} => {
  // ユーザーが要求している表示ページ数（無限スクロールで増える）。resetKey 変更でリセット。
  const [requestedPages, setRequestedPages] = React.useState(1);
  React.useEffect(() => {
    setRequestedPages(1);
  }, [resetKey]);

  useQueryError(studySource.error);

  const { displayed, needFetchStudyRecord } = computeRealtimeStream({
    studyRecords: studySource.items,
    hasNextStudyRecord: studySource.hasNextPage,
    pageSize: PER_PAGE,
    requestedPages,
  });

  // 補充: 要求ページ数を満たすために次ページが必要なら取得する。
  const {
    hasNextPage: studyHasNext,
    isFetchingNextPage: studyFetching,
    fetchNextPage: fetchNextStudy,
  } = studySource;
  React.useEffect(() => {
    if (needFetchStudyRecord && studyHasNext && !studyFetching) {
      fetchNextStudy();
    }
  }, [needFetchStudyRecord, studyHasNext, studyFetching, fetchNextStudy]);

  // 無限スクロール: さらに表示ページを要求する（不足分は上の補充 effect が取得する）。
  // もう表示・取得できるものが無ければインクリメントしない（末尾到達後の無駄な再計算を防ぐ）。
  useOnMainScrollAreaScroll(() => {
    if (studyFetching) {
      return;
    }
    const hasMore = studyHasNext || displayed.length < studySource.items.length;
    if (!hasMore) {
      return;
    }
    setRequestedPages((prev) => prev + 1);
  }, [studyFetching, studyHasNext, displayed.length, studySource.items.length]);

  return {
    displayed,
    isLoading: studySource.isLoading,
    isFetchingNextPage: studyFetching,
  };
};
